import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { takeEvery, call, put, select } from "redux-saga/effects"
import { post as apiPost } from "api"
import { get, toLower, omit, pickBy } from "lodash"
import { STATUSES } from "./constants"

// resourceUpdatedAt has been added to invalidate the cache when the resource is updated
export function generateSummaryKey({ account, action, modelId, resourceIdOrAlias, resourceType, resourceUpdatedAt, startDate, endDate, base }) {
  const baseSignature = get(base, "id", "")
  return btoa(`${account}${action}${modelId}${resourceIdOrAlias}${resourceType}${startDate}${endDate}${resourceUpdatedAt}${baseSignature}`)
}

export const { Types, Creators } = actionCreator("ompnt/modules/ai-summary", {
  generate: ["parameters"],
  generateSuccess: ["key", "summary", "timestamp"],
  generateError: ["key", "error"],
  updateStatus: ["key", "status"],
})

const INITIAL_STATE = {}

function handleGenerate(state, { parameters }) {
  const key = generateSummaryKey(parameters)
  if (get(state, [key, "status"]) !== STATUSES.IN_PROGRESS) {
    return { ...state, [key]: { status: STATUSES.NOT_STARTED } }
  }
  return state
}

function handleGenerateSuccess(state, { key, summary, timestamp }) {
  return { ...state, [key]: { timestamp, summary, status: STATUSES.DONE } }
}

function handleGenerateError(state, { key, error }) {
  return { ...state, [key]: { error } }
}

function handleUpdateStatus(state, { key, status }) {
  return { ...state, [key]: { status } }
}

export const HANDLERS = {
  [Types.GENERATE]: handleGenerate,
  [Types.GENERATE_SUCCESS]: handleGenerateSuccess,
  [Types.GENERATE_ERROR]: handleGenerateError,
  [Types.UPDATE_STATUS]: handleUpdateStatus,
}

export default createReducer(INITIAL_STATE, HANDLERS)

export function* fetchSummary({ parameters }) {
  const key = generateSummaryKey(parameters)

  const { account, action, modelId, resourceIdOrAlias, resourceType, startDate, endDate, base, timestamp, ...extraParameters } = omit(parameters, ["resourceUpdatedAt"])
  const url = `/api/${account}/ai-summarize/${action}`

  try {
    const { status } = yield select((state) => get(state, ["modules", "aiSummary", key], {}))
    if (status === STATUSES.NOT_STARTED) {
      // The AI Summary Lambda expects resourceId as a parameter name but it allows for id or alias
      yield put(Creators.updateStatus(key, STATUSES.IN_PROGRESS))
      const data = yield call(apiPost, url, { action, modelId, resourceId: resourceIdOrAlias, resourceType: toLower(resourceType), startDate, endDate, base, ...pickBy(extraParameters, (value) => value != null) })
      if (get(data, "error")) {
        // The call to the summary Lambda was successful but returned an error
        yield put(Creators.generateError(key, data.error))
      } else {
        yield put(Creators.generateSuccess(key, data, timestamp))
      }
    }
  } catch {
    yield put(Creators.generateError(key, "An error occurred while generating the summary. Refresh below."))
    return { error: true }
  }
}

export function* saga() {
  yield takeEvery(Types.GENERATE, fetchSummary)
}
